/* Admin · Partner Orgs management (Phase PP-Foundation · Feb 2026).

   Mounted at `/admin/partner-orgs`. Admins land here to create, manage,
   and invite users into partner orgs (banking, lender, insurance,
   carrier). Each org has 1 partner role + 1 dedicated portal URL.
*/
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Building2, Plus, Users, Banknote, Landmark, Shield, Truck, ExternalLink,
  RefreshCw, ShieldCheck, AlertTriangle, Lock, UnlockKeyhole, Trash2, Copy, Search,
  Link2, Clock, Ticket,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";

const TYPE_META = {
  banking:   { label: "Banking",   icon: Banknote, color: "sky"     },
  lender:    { label: "Lender",    icon: Landmark, color: "violet"  },
  insurance: { label: "Insurance", icon: Shield,   color: "emerald" },
  carrier:   { label: "Carrier",   icon: Truck,    color: "amber"   },
};

const STATUS_META = {
  active:   { label: "Active",   tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  disabled: { label: "Disabled", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  pending:  { label: "Pending",  tone: "bg-amber-50 text-amber-700 border-amber-200" },
};

function StatTile({ label, value, icon: Icon, tone = "slate" }) {
  return (
    <div className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3 flex items-center justify-between`}>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-0.5">{value}</div>
      </div>
      {Icon && <Icon className={`w-5 h-5 text-${tone}-500`} />}
    </div>
  );
}

function OrgRow({ org, onOpen }) {
  const meta = TYPE_META[org.type] || TYPE_META.banking;
  const status = STATUS_META[org.status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <button
      onClick={() => onOpen(org)}
      data-testid={`partner-org-row-${org.id}`}
      className="w-full text-left grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-slate-100 hover:bg-slate-50/70 transition"
    >
      <div className="col-span-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg grid place-items-center bg-${meta.color}-50 text-${meta.color}-600`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="font-medium text-slate-900">{org.name}</div>
          <div className="text-xs text-slate-500">{org.primary_contact_email || "—"}</div>
        </div>
      </div>
      <div className="col-span-2 text-sm">
        <Badge variant="outline" className={`bg-${meta.color}-50 text-${meta.color}-700 border-${meta.color}-200`}>
          {meta.label}
        </Badge>
      </div>
      <div className="col-span-2 text-sm">
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${status.tone}`}>{status.label}</span>
      </div>
      <div className="col-span-2 text-sm text-slate-700 tabular-nums">
        <Users className="w-3.5 h-3.5 inline mr-1 text-slate-400" /> {org.member_count ?? 0} member{(org.member_count ?? 0) !== 1 ? "s" : ""}
      </div>
      <div className="col-span-2 text-xs text-slate-500 text-right">
        Created {org.created_at ? new Date(org.created_at).toLocaleDateString() : "—"}
      </div>
    </button>
  );
}

function CreateOrgDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", type: "banking", primary_contact_email: "", website: "", notes: "", reason: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", type: "banking", primary_contact_email: "", website: "", notes: "", reason: "" }); }, [open]);

  const submit = async () => {
    if (!form.name.trim() || form.reason.trim().length < 3) return;
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/admin/partner-orgs", payload);
      toast.success(`Created ${data.org.name}`);
      onCreated(data.org);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Could not create partner org");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="partner-org-create-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> New partner org</DialogTitle>
          <DialogDescription>Adds a new external partner with isolated portal access.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="partner-org-create-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`partner-type-${k}`}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Name *</Label>
            <Input
              data-testid="partner-org-create-name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. TabaPay"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Primary contact email</Label>
              <Input
                data-testid="partner-org-create-email"
                value={form.primary_contact_email}
                onChange={e => setForm({ ...form, primary_contact_email: e.target.value })}
                placeholder="ops@partner.com"
              />
            </div>
            <div>
              <Label>Website</Label>
              <Input
                data-testid="partner-org-create-website"
                value={form.website}
                onChange={e => setForm({ ...form, website: e.target.value })}
                placeholder="https://…"
              />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              data-testid="partner-org-create-notes"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal context for support"
              rows={2}
            />
          </div>
          <div>
            <Label>Reason (≥3 chars) — required for audit</Label>
            <Textarea
              data-testid="partner-org-create-reason"
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="partner-org-create-submit"
            onClick={submit}
            disabled={busy || !form.name.trim() || form.reason.trim().length < 3}
          >
            {busy ? "Creating…" : "Create org"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteUserDialog({ open, onClose, org, onInvited }) {
  const [form, setForm] = useState({ email: "", name: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setForm({ email: "", name: "", reason: "" }); setResult(null); } }, [open]);

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim() || form.reason.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/partner-orgs/${org.id}/invite-user`, form);
      setResult(data);
      toast.success(`Invited ${data.user.email}`);
      onInvited?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Invite failed");
    } finally { setBusy(false); }
  };

  const copyAll = () => {
    if (!result) return;
    const txt = `Portal: ${window.location.origin}${result.portal_url}\nEmail: ${result.user.email}\nTemp password: ${result.temp_password}\n(Reset on first login.)`;
    navigator.clipboard.writeText(txt);
    toast.success("Credentials copied");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="partner-org-invite-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite partner user · {org?.name}</DialogTitle>
          <DialogDescription>Creates a partner-role user account and returns a one-time temporary password.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <div>
              <Label>Email *</Label>
              <Input
                data-testid="partner-invite-email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="user@partner.com"
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                data-testid="partner-invite-name"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                data-testid="partner-invite-reason"
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                rows={2}
              />
            </div>
          </div>
        ) : (
          <div data-testid="partner-invite-success" className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-sm font-medium text-emerald-700 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Invitation sent · share the credentials below securely
            </div>
            <div className="font-mono text-xs text-slate-700 leading-relaxed">
              <div><strong>Portal:</strong> {window.location.origin}{result.portal_url}</div>
              <div><strong>Email:</strong> {result.user.email}</div>
              <div><strong>Temp password:</strong> {result.temp_password}</div>
            </div>
            <Button size="sm" variant="outline" onClick={copyAll} data-testid="partner-invite-copy">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy all
            </Button>
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                data-testid="partner-invite-submit"
                onClick={submit}
                disabled={busy || !form.email.trim() || !form.name.trim() || form.reason.trim().length < 3}
              >
                {busy ? "Inviting…" : "Send invite"}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DemoLinkDialog({ open, onClose, org }) {
  const [form, setForm] = useState({ hours: 24, max_uses: 5, reason: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [links, setLinks] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const loadLinks = async () => {
    if (!org?.id) return;
    setLoadingList(true);
    try {
      const { data } = await api.get(`/admin/partner-orgs/${org.id}/demo-links`);
      setLinks(data.items || []);
    } catch { /* ignore */ }
    finally { setLoadingList(false); }
  };

  useEffect(() => {
    if (open) {
      setForm({ hours: 24, max_uses: 5, reason: "" });
      setResult(null);
      loadLinks();
    }
    // eslint-disable-next-line
  }, [open]);

  const submit = async () => {
    if (form.reason.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/partner-orgs/${org.id}/demo-link`, {
        hours: Number(form.hours),
        max_uses: Number(form.max_uses),
        reason: form.reason.trim(),
      });
      setResult(data);
      toast.success("Demo link minted");
      loadLinks();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Could not mint link");
    } finally { setBusy(false); }
  };

  const copyUrl = () => {
    if (!result?.url) return;
    navigator.clipboard.writeText(result.url);
    toast.success("Demo link copied");
  };

  const revoke = async (link_id) => {
    if (!window.confirm("Revoke this demo link? Anyone holding the URL will lose access immediately.")) return;
    try {
      await api.post(`/admin/partner-demo-links/${link_id}/revoke`);
      toast.success("Link revoked");
      loadLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Revoke failed");
    }
  };

  const fmtRemaining = (iso) => {
    if (!iso) return "—";
    const diffMs = new Date(iso).getTime() - Date.now();
    if (diffMs <= 0) return "expired";
    const h = Math.floor(diffMs / 3_600_000);
    const m = Math.floor((diffMs % 3_600_000) / 60_000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const status = (lnk) => {
    if (lnk.revoked) return { label: "Revoked", tone: "bg-rose-50 text-rose-700 border-rose-200" };
    if (new Date(lnk.expires_at).getTime() <= Date.now())
      return { label: "Expired", tone: "bg-slate-100 text-slate-600 border-slate-200" };
    if (lnk.used_count >= lnk.max_uses)
      return { label: "Used up", tone: "bg-amber-50 text-amber-700 border-amber-200" };
    return { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="partner-org-demo-link-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Demo link · {org?.name}
          </DialogTitle>
          <DialogDescription>
            Mint a one-click, time-bound, <strong>read-only</strong> magic link a prospect can use to preview this portal — no password needed.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valid for (hours)</Label>
                <Input
                  data-testid="partner-demo-hours"
                  type="number"
                  min={1}
                  max={168}
                  value={form.hours}
                  onChange={e => setForm({ ...form, hours: e.target.value })}
                />
                <div className="text-[11px] text-slate-500 mt-1">1–168 (up to 7 days)</div>
              </div>
              <div>
                <Label>Max uses</Label>
                <Input
                  data-testid="partner-demo-max-uses"
                  type="number"
                  min={1}
                  max={100}
                  value={form.max_uses}
                  onChange={e => setForm({ ...form, max_uses: e.target.value })}
                />
                <div className="text-[11px] text-slate-500 mt-1">Total sessions allowed</div>
              </div>
            </div>
            <div>
              <Label>Reason (≥3 chars) — required for audit</Label>
              <Textarea
                data-testid="partner-demo-reason"
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                rows={2}
                placeholder="e.g. Demo for prospect Acme Capital"
              />
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900 flex gap-2">
              <Shield className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Demo viewer is fully <strong>read-only</strong> · cannot top up wallets, approve loans, or change settings · all data is scoped to this org · session auto-expires.
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="partner-demo-success" className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-sm font-medium text-emerald-700 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Magic link ready · share with your prospect
            </div>
            <div className="font-mono text-xs text-slate-800 break-all bg-white border rounded-md px-3 py-2">
              {result.url}
            </div>
            <div className="text-xs text-slate-600 grid grid-cols-2 gap-y-1">
              <div><strong>Expires:</strong> {new Date(result.expires_at).toLocaleString()}</div>
              <div><strong>Max uses:</strong> {result.max_uses}</div>
              <div className="col-span-2"><strong>Portal:</strong> {result.portal_url}</div>
            </div>
            <Button size="sm" variant="outline" onClick={copyUrl} data-testid="partner-demo-copy">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
            </Button>
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> This is the only time we'll show the full URL. Copy it now — we only store a prefix for auditing.
            </div>
          </div>
        )}

        {/* Active links table */}
        <div className="border-t pt-3 mt-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
            <span>Recent demo links</span>
            <button onClick={loadLinks} className="text-slate-400 hover:text-slate-700">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="rounded-lg border divide-y max-h-56 overflow-y-auto" data-testid="partner-demo-links-list">
            {links.length === 0 && (
              <div className="text-center text-xs text-slate-500 py-4">No demo links yet.</div>
            )}
            {links.map(lnk => {
              const st = status(lnk);
              return (
                <div key={lnk.id} className="px-3 py-2 flex items-center gap-3 text-xs">
                  <Ticket className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-slate-700 truncate">
                      …{lnk.token_prefix}…
                      <span className={`ml-2 px-1.5 py-0.5 rounded border text-[10px] ${st.tone}`}>{st.label}</span>
                    </div>
                    <div className="text-slate-500 flex items-center gap-3 mt-0.5">
                      <span><Clock className="w-3 h-3 inline mr-0.5" /> {fmtRemaining(lnk.expires_at)}</span>
                      <span>{lnk.used_count}/{lnk.max_uses} used</span>
                    </div>
                  </div>
                  {!lnk.revoked && new Date(lnk.expires_at).getTime() > Date.now() && (
                    <Button size="sm" variant="ghost" onClick={() => revoke(lnk.id)} data-testid={`partner-demo-revoke-${lnk.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button
                data-testid="partner-demo-submit"
                onClick={submit}
                disabled={busy || form.reason.trim().length < 3}
              >
                {busy ? "Minting…" : "Mint demo link"}
              </Button>
            </>
          ) : (
            <Button onClick={() => setResult(null)} variant="outline">Mint another</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrgDetailDrawer({ org, onClose, onMutate }) {
  const [detail, setDetail] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!org) return;
    const { data } = await api.get(`/admin/partner-orgs/${org.id}`);
    setDetail(data);
  };
  useEffect(() => { setDetail(null); load(); /* eslint-disable-next-line */ }, [org?.id]);

  if (!org) return null;
  const TypeIcon = (TYPE_META[org.type] || TYPE_META.banking).icon;

  const onDisableOrg = async () => {
    const reason = window.prompt("Reason for disabling (≥3 chars):");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/admin/partner-orgs/${org.id}/disable`, { reason });
      toast.success(`Disabled ${org.name}`);
      onMutate?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Disable failed");
    } finally { setBusy(false); }
  };

  const onEnableOrg = async () => {
    const reason = window.prompt("Reason for re-enabling (≥3 chars):");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/admin/partner-orgs/${org.id}/enable`, { reason });
      toast.success(`Re-enabled ${org.name}`);
      onMutate?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Enable failed");
    } finally { setBusy(false); }
  };

  const onDisableMember = async (uid, email) => {
    const reason = window.prompt(`Reason for disabling ${email} (≥3 chars):`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/admin/partner-orgs/${org.id}/users/${uid}/disable`, { reason });
      toast.success(`Disabled ${email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Disable failed");
    }
  };

  return (
    <Dialog open={!!org} onOpenChange={onClose}>
      <DialogContent data-testid="partner-org-drawer" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TypeIcon className="w-4 h-4" />
            <span data-testid="partner-org-drawer-name">{org.name}</span>
          </DialogTitle>
          <DialogDescription>{detail?.role_def?.description}</DialogDescription>
        </DialogHeader>

        {detail && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Type</div>
                <div className="text-slate-900 mt-0.5">{TYPE_META[org.type]?.label || org.type}</div>
              </div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Status</div>
                <div className="text-slate-900 mt-0.5">{STATUS_META[org.status]?.label || org.status}</div>
              </div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Primary contact</div>
                <div className="text-slate-900 mt-0.5">{org.primary_contact_email || "—"}</div>
              </div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Portal URL</div>
                <div className="text-slate-900 mt-0.5">
                  <a href={detail.role_def.portal} className="underline text-sky-600" target="_blank" rel="noreferrer">
                    {detail.role_def.portal} <ExternalLink className="w-3 h-3 inline ml-0.5" />
                  </a>
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">Scopes granted ({detail.role_def.scopes.length})</div>
              <div className="flex flex-wrap gap-1.5" data-testid="partner-org-scopes">
                {detail.role_def.scopes.map(s => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">{s}</span>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-slate-700">Members ({detail.members.length})</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowDemo(true)} data-testid="partner-org-demo-link-btn">
                    <Link2 className="w-3.5 h-3.5 mr-1" /> Demo link
                  </Button>
                  <Button size="sm" onClick={() => setShowInvite(true)} data-testid="partner-org-invite-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Invite user
                  </Button>
                </div>
              </div>
              <div className="rounded-lg border divide-y" data-testid="partner-org-members">
                {detail.members.length === 0 && (
                  <div className="text-center text-sm text-slate-500 py-6">No members yet.</div>
                )}
                {detail.members.map(m => (
                  <div key={m.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.email}</div>
                    </div>
                    {m.suspended ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200">Suspended</span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Active</span>
                    )}
                    {!m.suspended && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDisableMember(m.id, m.email)}
                        data-testid={`partner-org-disable-member-${m.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {org.status === "active" ? (
              <Button variant="outline" onClick={onDisableOrg} disabled={busy} data-testid="partner-org-disable">
                <Lock className="w-4 h-4 mr-1.5" /> Disable org
              </Button>
            ) : (
              <Button variant="outline" onClick={onEnableOrg} disabled={busy} data-testid="partner-org-enable">
                <UnlockKeyhole className="w-4 h-4 mr-1.5" /> Re-enable
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>

        <InviteUserDialog
          open={showInvite}
          onClose={() => setShowInvite(false)}
          org={org}
          onInvited={() => { load(); onMutate?.(); }}
        />

        <DemoLinkDialog
          open={showDemo}
          onClose={() => setShowDemo(false)}
          org={org}
        />
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPartnerOrgs() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ by_type: {}, by_status: {} });
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [openOrg, setOpenOrg] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") params.type = filterType;
      if (filterStatus !== "all") params.status = filterStatus;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/partner-orgs", { params });
      setRows(data.rows);
      setStats({ by_type: data.by_type || {}, by_status: data.by_status || {} });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterStatus]);

  const totalMembers = useMemo(() => rows.reduce((acc, r) => acc + (r.member_count || 0), 0), [rows]);

  return (
    <div data-testid="admin-partner-orgs" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Building2 className="w-6 h-6 text-sky-500" /> Partner Orgs
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage external partner organisations (banks · lenders · insurers · carriers) and their isolated portal users.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="partner-orgs-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="partner-orgs-new">
            <Plus className="w-4 h-4 mr-1.5" /> New partner org
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="partner-orgs-kpis">
        <StatTile label="Total orgs"   value={rows.length}                     icon={Building2} tone="slate" />
        <StatTile label="Banking"      value={stats.by_type.banking ?? 0}      icon={Banknote}  tone="sky" />
        <StatTile label="Lender"       value={stats.by_type.lender ?? 0}       icon={Landmark}  tone="violet" />
        <StatTile label="Insurance"    value={stats.by_type.insurance ?? 0}    icon={Shield}    tone="emerald" />
        <StatTile label="Carrier"      value={stats.by_type.carrier ?? 0}      icon={Truck}     tone="amber" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-slate-100">
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute top-2.5 left-2.5 text-slate-400 pointer-events-none" />
            <Input
              data-testid="partner-orgs-search"
              placeholder="Search by name"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === "Enter" && load()}
              className="pl-8 h-9"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="partner-orgs-filter-type" className="w-40 h-9"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="partner-orgs-filter-status" className="w-36 h-9"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500 self-center" data-testid="partner-orgs-total-members">
            {totalMembers} member{totalMembers !== 1 ? "s" : ""} across {rows.length} org{rows.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div data-testid="partner-orgs-table">
          {rows.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-12 flex flex-col items-center gap-3" data-testid="partner-orgs-empty">
              <AlertTriangle className="w-6 h-6 text-slate-400" />
              No partner orgs yet — click "New partner org" to add the first one.
            </div>
          )}
          {rows.map(o => <OrgRow key={o.id} org={o} onOpen={setOpenOrg} />)}
        </div>
      </div>

      <CreateOrgDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
      <OrgDetailDrawer org={openOrg} onClose={() => setOpenOrg(null)} onMutate={load} />
    </div>
  );
}
